import React, { useEffect, useState } from "react"
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from "@material-ui/core"
import ClearIcon from '@material-ui/icons/Clear'
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css'
import formStyles from '../TeamsManagement/styles.module.css'
import styles from './styles.module.css'
import { BlueBtn, BlueInverseBtn, GreenBtn, RedBtn } from "../Styled/StyledButtons"

export type DialogTone = 'confirm' | 'clear' | 'neutral'

interface IProps {
    show: boolean
    title: string
    /** Shown above the comment box — what this verdict will actually do. */
    description?: React.ReactNode
    /** Clearing or unblocking withdraws a suspicion, so the API demands a comment.
     *  Confirming may be comment-free. */
    commentRequired: boolean
    actionLabel: string
    tone: DialogTone
    toggleHandle: () => void
    onSubmit: (comment: string) => void
}

/**
 * The one dialog behind confirm / clear / unblock, single and bulk.
 *
 * Kept as one component because the difference between those actions is a label, a colour
 * and whether the comment is mandatory — three separate near-identical dialogs would drift
 * apart on the part that matters (the comment rule).
 */
const CommentDialog = (props: IProps) => {
    const [comment, setComment] = useState('')

    // Reset between openings, otherwise the previous verdict's comment is pre-filled and
    // gets attached to a different decision.
    useEffect(() => {
        if (props.show) {
            setComment('')
        }
    }, [props.show])

    const valid = !props.commentRequired || comment.trim().length > 3

    const submit = () => {
        if (!valid) {
            return
        }
        props.onSubmit(comment.trim())
        props.toggleHandle()
    }

    const ActionBtn = props.tone === 'clear'
        ? RedBtn
        : (props.tone === 'confirm' ? GreenBtn : BlueBtn)

    return (
        <Dialog open={props.show} onClose={props.toggleHandle} maxWidth="sm" fullWidth={true}>
            <DialogTitle>
                {props.title}
                <IconButton className={mainStyles.floatRight} onClick={props.toggleHandle}>
                    <ClearIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={formStyles.popup}>
                <Grid container spacing={1}>
                    {props.description ?
                        <Grid item xs={12}>
                            <div className={styles.Evidence}>{props.description}</div>
                        </Grid>
                        : null}
                    <Grid item xs={12}>
                        <label className={formStyles.FormLabel}>
                            Comment{props.commentRequired ? ' (required)' : ' (optional)'}
                        </label>
                        <input
                            placeholder="Why?"
                            className={`${(props.commentRequired && !valid) ? formStyles.FormInvalid : ""} ${formStyles.FormControl}`}
                            value={comment}
                            onChange={(event) => setComment(event.target.value)}
                        />
                        {props.commentRequired ?
                            <div className={styles.Hint}>
                                Recorded against your user in the number's history.
                            </div>
                            : null}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <BlueInverseBtn fullWidth={true} onClick={props.toggleHandle}>Cancel</BlueInverseBtn>
                    </Grid>
                    <Grid item xs={6}>
                        <ActionBtn fullWidth={true} disabled={!valid} onClick={submit}>
                            {props.actionLabel}
                        </ActionBtn>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    )
}

export default CommentDialog
